import { useCallback, useEffect, useState } from 'react'
import { adminRequest } from './EshopCategories'
import DeliverySettingsPanel from './DeliverySettingsPanel'

type Rate = { id: string; municipality: string; district: string; priceCents: number | null; active: boolean }

const euro = (cents: number) => (cents / 100).toLocaleString('sk-SK', { style: 'currency', currency: 'EUR' })
const norm = (value: string) => value.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')
const toCents = (value: string): number | null | typeof NaN => {
  const text = value.trim().replace(',', '.').replace(/\s|€/g, '')
  if (!text) return null
  const n = Number(text)
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN
}

export default function DeliveryRatesAdmin() {
  const [rates, setRates] = useState<Rate[]>([])
  const [district, setDistrict] = useState('Prievidza')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'priced' | 'unpriced' | 'off'>('all')
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newPrice, setNewPrice] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const data = await adminRequest('/api/admin/delivery-rates')
      setRates(Array.isArray(data?.rates) ? data.rates : [])
      if (typeof data?.district === 'string') setDistrict(data.district)
    } catch (e) { setError(e instanceof Error ? e.message : 'Cenník sa nepodarilo načítať.') }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { void load() }, [load])

  async function update(rate: Rate, patch: Partial<Pick<Rate, 'priceCents' | 'active'>>) {
    setSavingId(rate.id); setError(''); setMessage('')
    try {
      const data = await adminRequest(`/api/admin/delivery-rates/${encodeURIComponent(rate.id)}`, { method: 'PATCH', body: JSON.stringify(patch) })
      setRates(current => current.map(r => r.id === rate.id ? data.rate : r))
      setDrafts(current => { const next = { ...current }; delete next[rate.id]; return next })
      setMessage(`${rate.municipality}: uložené.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie sa nepodarilo.') }
    finally { setSavingId(null) }
  }

  function commitPrice(rate: Rate) {
    const draft = drafts[rate.id]
    if (draft === undefined) return
    const cents = toCents(draft)
    if (Number.isNaN(cents)) { setError('Cena musí byť číslo, napr. 3,50.'); return }
    if (cents === rate.priceCents) { setDrafts(current => { const next = { ...current }; delete next[rate.id]; return next }); return }
    void update(rate, { priceCents: cents as number | null })
  }

  async function add(event: React.FormEvent) {
    event.preventDefault()
    const cents = toCents(newPrice)
    if (Number.isNaN(cents)) { setError('Cena musí byť číslo, napr. 3,50.'); return }
    setError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/delivery-rates', { method: 'POST', body: JSON.stringify({ municipality: newName, priceCents: cents }) })
      setRates(data.rates)
      setMessage(`Obec „${data.rate.municipality}“ je pridaná.`)
      setNewName(''); setNewPrice('')
    } catch (e) { setError(e instanceof Error ? e.message : 'Pridanie sa nepodarilo.') }
  }

  async function remove(rate: Rate) {
    if (!window.confirm(`Odstrániť obec „${rate.municipality}“ z cenníka? Doručenie do nej sa potom bude naceňovať individuálne.`)) return
    setError(''); setMessage('')
    try {
      const data = await adminRequest(`/api/admin/delivery-rates/${encodeURIComponent(rate.id)}`, { method: 'DELETE' })
      setRates(data.rates)
      setMessage(`Obec „${rate.municipality}“ je odstránená.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Odstránenie sa nepodarilo.') }
  }

  const q = norm(search.trim())
  const counts = {
    all: rates.length,
    priced: rates.filter(r => r.active && r.priceCents !== null).length,
    unpriced: rates.filter(r => r.active && r.priceCents === null).length,
    off: rates.filter(r => !r.active).length,
  }
  const visible = rates.filter(r => (!q || norm(r.municipality).includes(q))
    && (filter === 'all' || (filter === 'priced' ? r.active && r.priceCents !== null : filter === 'unpriced' ? r.active && r.priceCents === null : !r.active)))
  const priced = rates.filter(r => r.active && r.priceCents !== null).map(r => r.priceCents as number)

  return <>
  <DeliverySettingsPanel />
  <div className="panel delivery-admin">
    <div className="pa-head">
      <div><h2>Cenník doručenia na adresu – okres {district}</h2><p>Zákazník zadá obec a e-shop mu ukáže cenu. Obec bez ceny = naceníme individuálne.</p></div>
      <div className="pa-head-actions"><button type="button" className="pa-ghost" onClick={() => void load()} disabled={loading}>↻ Obnoviť</button></div>
    </div>

    <div className="st-summary">
      <div className="is-ok"><strong>{counts.priced}</strong><span>obcí s doručením</span></div>
      <div className={counts.unpriced ? 'is-warn' : ''}><strong>{counts.unpriced}</strong><span>bez ceny (naceníme)</span></div>
      <div><strong>{priced.length ? `${euro(Math.min(...priced))} – ${euro(Math.max(...priced))}` : '—'}</strong><span>rozpätie cien</span></div>
      <div><strong>{counts.off}</strong><span>vypnuté</span></div>
    </div>

    <form className="dl-add" onSubmit={add}>
      <input placeholder="Nová obec, napr. Nováky" value={newName} maxLength={80} onChange={e => setNewName(e.target.value)} aria-label="Názov obce" />
      <span className="dl-price-input"><input placeholder="Cena" inputMode="decimal" value={newPrice} onChange={e => setNewPrice(e.target.value)} aria-label="Cena doručenia v eurách" /><span>€</span></span>
      <button className="primary-btn" disabled={newName.trim().length < 2}>＋ Pridať obec</button>
    </form>

    <div className="or-toolbar dl-toolbar">
      <label className="pa-search"><span aria-hidden="true">⌕</span><input placeholder="Hľadať obec…" value={search} onChange={e => setSearch(e.target.value)} />{search && <button type="button" aria-label="Vymazať" onClick={() => setSearch('')}>×</button>}</label>
      <div className="pa-tabs">
        {([['all', 'Všetky'], ['priced', 'S cenou'], ['unpriced', 'Bez ceny'], ['off', 'Vypnuté']] as Array<[typeof filter, string]>).map(([id, label]) =>
          <button key={id} type="button" className={filter === id ? 'is-on' : ''} onClick={() => setFilter(id)}>{label}<span>{counts[id]}</span></button>)}
      </div>
    </div>

    {error && <p className="error-message" role="alert">{error}</p>}
    {message && <p className="pa-message" role="status">{message}</p>}

    {loading ? <p className="empty-message">Načítavam cenník…</p> : !visible.length ? <div className="pa-empty"><span>♡</span><strong>{rates.length ? 'Žiadna obec nevyhovuje' : 'Cenník je zatiaľ prázdny – pridajte prvú obec.'}</strong></div> :
      <div className="pa-table-wrap"><table className="pa-table dl-table">
        <thead><tr><th>Obec</th><th>Cena doručenia</th><th className="pa-col-eshop">Ponúkať</th><th aria-label="Akcie" /></tr></thead>
        <tbody>{visible.map(rate => {
          const draft = drafts[rate.id]
          const value = draft ?? (rate.priceCents === null ? '' : (rate.priceCents / 100).toFixed(2).replace('.', ','))
          return <tr key={rate.id} className={rate.active ? '' : 'is-inactive'}>
            <td><strong>{rate.municipality}</strong></td>
            <td>
              <span className={`dl-price-input ${draft !== undefined ? 'is-dirty' : ''}`}>
                <input aria-label={`Cena pre ${rate.municipality}`} inputMode="decimal" placeholder="naceníme" value={value} disabled={savingId === rate.id}
                  onChange={e => setDrafts(current => ({ ...current, [rate.id]: e.target.value }))}
                  onBlur={() => commitPrice(rate)}
                  onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDrafts(current => { const next = { ...current }; delete next[rate.id]; return next }) }} />
                <span>€</span>
              </span>
            </td>
            <td className="pa-col-eshop">
              <button type="button" role="switch" aria-checked={rate.active} aria-label={`Ponúkať doručenie do ${rate.municipality}`} className={`pa-switch ${rate.active ? 'is-on' : ''}`} disabled={savingId === rate.id} onClick={() => void update(rate, { active: !rate.active })}><span /></button>
            </td>
            <td className="dl-actions"><button type="button" className="pa-link dl-remove" onClick={() => void remove(rate)}>Odstrániť</button></td>
          </tr>
        })}</tbody>
      </table></div>}
    <p className="st-note">Cenu upravíte priamo v riadku – uloží sa po kliknutí mimo poľa alebo klávesom Enter. Prázdna cena znamená, že cenu do obce naceníte pri objednávke (ak je to vyššie zapnuté).</p>
  </div>
  </>
}
